import { type FC, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import ReCaptcha from 'react-google-recaptcha'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { type UseMutationResult, useQuery, useQueryClient } from 'react-query'
import * as z from 'zod'

import { CreatorHeroLogo } from '@/components/layout/header/logo'
import { Button } from '@/components/ui/button.tsx'
import { FormField } from '@/components/ui/form/form-field.tsx'
import { useToast } from '@/components/ui/toast'
import { authService } from '@/modules/accounts/service/auth-service.ts'
import type {
  AccountAuthResponse,
  AccountLoginRequest,
} from '@/modules/accounts/types.ts'
import { AccountTfaAuthenticateForm } from '@/modules/accounts/ui/AccountTfaAuthenticateForm.tsx'
import { emailSchema } from '@/types/schemas.ts'
import { CAPTCHA_SITE_KEY } from '@/utils/constants'
import { getErrorMessage } from '@/utils/error'

export type LoginFormValues = z.infer<typeof loginFormSchema>

const loginFormSchema = z
  .object({
    email: emailSchema,
    password: z.string().min(1, { message: 'Please enter password' }),
    'g-recaptcha-response': z.string().optional(),
    isCaptchaRequired: z.boolean(),
  })
  .refine((data) => !data.isCaptchaRequired || !!data['g-recaptcha-response'], {
    path: ['g-recaptcha-response'],
    message: 'Captcha is required',
  })

interface Props {
  onFinish?: (values: { email: string; token: string }) => void
  loginMutation: UseMutationResult<
    AccountAuthResponse,
    unknown,
    AccountLoginRequest,
    unknown
  >
}

export const LoginForm: FC<Props> = ({ onFinish, loginMutation }) => {
  const queryClient = useQueryClient()
  const toast = useToast()

  const form = useForm<LoginFormValues>({
    mode: 'onChange',
    resolver: zodResolver(loginFormSchema),
  })

  const {
    handleSubmit,
    formState: { errors },
    setError,
  } = form

  useQuery('isCaptchaRequired', authService.checkIsCaptchaRequired, {
    onSuccess: ({ requiresCaptcha }) => {
      form.setValue('isCaptchaRequired', requiresCaptcha)
    },
    cacheTime: 0,
    refetchInterval: 60000,
  })

  const [showTfaAuthenticateDialog, setShowTfaAuthenticateDialog] =
    useState(false)
  const [tfaSetupRequired, setTfaSetupRequired] = useState(false)
  const [email, setEmail] = useState('')
  const [token, setToken] = useState('')

  const isCaptchaRequired = useWatch({
    control: form.control,
    name: 'isCaptchaRequired',
  })

  const onSubmit = async (values: LoginFormValues) => {
    const loginOptions = {
      email: values.email,
      password: values.password,
      'g-recaptcha-response': values['g-recaptcha-response'],
    }

    try {
      const authResponse = await loginMutation.mutateAsync(loginOptions)

      if (authResponse['tfa-enabled']) {
        setEmail(values.email)
        setToken(authResponse['auth-token']!)
        setShowTfaAuthenticateDialog(true)
      } else if (authResponse['tfa-setup-required']) {
        setEmail(values.email)
        setTfaSetupRequired(true)
        setToken(authResponse['auth-token']!)
      } else {
        onFinish?.({ email: values.email, token: authResponse['auth-token']! })
      }
    } catch (error) {
      try {
        window.grecaptcha?.reset()
      } catch (error) {
        console.error('Error resetting grecaptcha:', error)
      }
      queryClient.invalidateQueries('isCaptchaRequired')

      setError('root', {
        type: 'server',
        message: getErrorMessage(error),
      })
    }
  }

  if (showTfaAuthenticateDialog || tfaSetupRequired) {
    return (
      <AccountTfaAuthenticateForm
        email={email}
        token={token}
        isSetupRequired={tfaSetupRequired}
        onFinish={() => onFinish?.({ email, token })}
      />
    )
  }

  return (
    <>
      <header className="mb-10 flex flex-col items-center">
        <CreatorHeroLogo
          useLink={false}
          width={215}
        />
        <p className="text-text-sub mt-3 text-sm font-medium">
          Enter your details below to log into your account
        </p>
      </header>
      <div>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {errors.root && (
              <div
                className="bg-danger-light text-danger-default mb-4 w-full rounded-md py-1.5 text-center text-sm"
                data-testid="login-error"
              >
                {errors.root?.message}
              </div>
            )}

            <div className="mb-14 flex flex-col gap-6">
              <FormField
                testId="email-input"
                name="email"
                label="E-Mail Address"
                placeholder="Enter E-Mail"
              />

              <FormField
                testId="password-input"
                type="password"
                name="password"
                label="Password"
                isPassword
                placeholder="Enter Password"
                showErrors
              />

              <Controller
                control={form.control}
                name="isCaptchaRequired"
                defaultValue={false}
                render={({ field: { value, onChange } }) => (
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={onChange}
                    hidden
                  />
                )}
              />

              {isCaptchaRequired && (
                <Controller
                  control={form.control}
                  name="g-recaptcha-response"
                  render={({ field }) => (
                    <ReCaptcha
                      sitekey={CAPTCHA_SITE_KEY}
                      onChange={(value) => {
                        if (!value) {
                          return
                        }

                        field.onChange(value)
                      }}
                    />
                  )}
                />
              )}
            </div>

            <Button
              testId="login-submit-button"
              className="mb-3 w-full"
              isLoading={loginMutation.isLoading}
            >
              Log In
            </Button>
          </form>
        </FormProvider>
      </div>
    </>
  )
}
